import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Stack,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
} from '@mui/material';
import {
  Today,
  EventNote,
  Schedule,
  CompareArrows,
  ArrowForward,
  PersonAdd,
} from '@mui/icons-material';
import { teacherModuleAPI, notificationsAPI } from '../../services/api';
import { useSupervisorTeacherContext } from '../../contexts/SupervisorTeacherContext';
import TeacherWeeklyTimetable from '../../components/Teacher/TeacherWeeklyTimetable';
import TeacherDayCheckinCard from '../../components/Teacher/TeacherDayCheckinCard';
import NewsUpdatesPanel from '../../components/News/NewsUpdatesPanel';
import { toAppPath } from '../../utils/notificationPaths';
import { formatDemoDualCaption, getTodayYMDInTimezone, getViewerTimezone, getWeekStartInTimezone, shiftLocalYMD } from '../../utils/dateTimeUtils';
import { useAuth } from '../../contexts/AuthContext';

function getWeekEnd(weekStartYmd) {
  const [y, m, d] = weekStartYmd.split('-').map(Number);
  return shiftLocalYMD(6, new Date(y, m - 1, d));
}

const TeacherHome = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const picker = useSupervisorTeacherContext();
  const teacherIdParam = picker.isSupervisorPicker ? picker.selectedTeacherId : undefined;
  const calendarTz = getViewerTimezone(picker.selectedTeacherTimezone || user?.timezone);
  const [weekStart, setWeekStart] = useState(() => getWeekStartInTimezone(calendarTz));

  useEffect(() => {
    setWeekStart(getWeekStartInTimezone(calendarTz));
  }, [calendarTz]);

  const homeQuery = useQuery(
    ['teacherHome', weekStart, teacherIdParam, calendarTz],
    async () => {
      const params = { weekStart, viewer_timezone: calendarTz };
      if (teacherIdParam) params.teacherId = teacherIdParam;
      const res = await teacherModuleAPI.getHome(params);
      return res.data?.data;
    },
    { enabled: picker.ready }
  );

  const pendingQuery = useQuery(
    ['teacherMyPendingItems', teacherIdParam],
    async () => {
      const params = teacherIdParam ? { teacherId: teacherIdParam } : undefined;
      const res = await teacherModuleAPI.getMyPendingItems(params);
      const items = res.data?.data || [];
      return {
        items,
        total: typeof res.data?.total === 'number' ? res.data.total : items.length,
      };
    },
    { enabled: picker.ready }
  );

  const showOwnNotifications = !picker.isSupervisorPicker || picker.viewingOwnSchedule;
  const showOwnCheckin = showOwnNotifications;
  const updatesQuery = useQuery(
    ['notifications-important-updates'],
    async () => {
      const res = await notificationsAPI.getImportantUpdates();
      return res.data?.data || [];
    },
    { enabled: picker.ready && showOwnNotifications, refetchInterval: 60000 }
  );

  const data = homeQuery.data;
  const todayYmd = getTodayYMDInTimezone(calendarTz);
  const weekEnd = getWeekEnd(weekStart);
  const demosFrom = todayYmd;
  const [ty, tm, td] = todayYmd.split('-').map(Number);
  const demosTo = shiftLocalYMD(29, new Date(ty, tm - 1, td));
  const teacherIdForDemos = data?.viewingTeacherId || data?.ownTeacherId || teacherIdParam;
  const makeupInWeekCount = useMemo(
    () => (data?.weeklySessions || []).filter((s) => s.session_kind === 'makeup').length,
    [data?.weeklySessions]
  );
  const upcomingMakeupRows = useMemo(() => {
    const fromApi = data?.upcomingMakeups || [];
    if (fromApi.length) return fromApi;
    return (data?.weeklySessions || []).filter(
      (s) => s.session_kind === 'makeup' && s.id && String(s.session_date || '').slice(0, 10) >= todayYmd
    );
  }, [data?.upcomingMakeups, data?.weeklySessions, todayYmd]);
  // (weeklySessions/days/shiftWeek) removed — calendar is now shared component

  if (picker.isSupervisorPicker && picker.teachersLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (picker.isSupervisorPicker && !picker.ready) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Teacher Home
        </Typography>
        {picker.teachersError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not load the teacher list. Please refresh or contact support.
          </Alert>
        ) : null}
        {!picker.teachersLoading && picker.teachers.length === 0 ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No active teachers found.
          </Alert>
        ) : (
          <Alert severity="info">Select a teacher to view their schedule and dashboard.</Alert>
        )}
        <FormControl fullWidth sx={{ mt: 2, maxWidth: 420 }} disabled={picker.teachers.length === 0}>
          <InputLabel id="supervisor-teacher-select-label">Teacher</InputLabel>
          <Select
            labelId="supervisor-teacher-select-label"
            label="Teacher"
            value={picker.selectedTeacherId || ''}
            onChange={(e) => picker.setSelectedTeacherId(e.target.value)}
          >
            {picker.teachers.map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  if (homeQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (homeQuery.isError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load teacher dashboard.</Typography>
      </Box>
    );
  }

  const pendingRows = pendingQuery.data?.items || [];
  const pendingTotal = pendingQuery.data?.total ?? pendingRows.length;
  const displayedPendingCount = Math.min(pendingRows.length, 10);
  const supervisorReadOnly = picker.isSupervisorPicker;

  const getPendingSummary = (missing) => {
    const parts = [];
    if (missing?.cancelClass) parts.push('Cancel class');
    if (missing?.attendance) parts.push('Attendance');
    if (missing?.homework) parts.push('Homework');
    if (missing?.batchNotes || missing?.classLog) parts.push('Class Log');
    if (missing?.nextPlan) parts.push('Next class plan');
    return parts;
  };

  const navigateToPending = (row) => {
    if (!row?.sessionId) return;
    if (row?.missing?.cancelClass) {
      navigate(
        `/batches/${encodeURIComponent(row.batchId)}?sessionId=${encodeURIComponent(row.sessionId)}&action=cancel`
      );
      return;
    }
    const missingCount =
      (row?.missing?.attendance ? 1 : 0) +
      (row?.missing?.homework ? 1 : 0) +
      (row?.missing?.batchNotes || row?.missing?.classLog ? 1 : 0) +
      (row?.missing?.nextPlan ? 1 : 0);

    // If multiple items are missing, open batch details and let user choose.
    if (missingCount > 1) {
      navigate(`/batches/${encodeURIComponent(row.batchId)}?sessionId=${encodeURIComponent(row.sessionId)}`);
      return;
    }

    const action = row?.missing?.attendance
      ? 'attendance'
      : row?.missing?.homework
        ? 'homework'
        : row?.missing?.batchNotes || row?.missing?.classLog
          ? 'notes'
          : row?.missing?.nextPlan
            ? 'nextPlan'
            : 'completion';

    navigate(
      `/batches/${encodeURIComponent(row.batchId)}?sessionId=${encodeURIComponent(row.sessionId)}&action=${encodeURIComponent(action)}`
    );
  };

  return (
    <Box>
      {showOwnCheckin && !data?.missingTeacherProfile ? <TeacherDayCheckinCard /> : null}

      <Box sx={{ mb: 2 }}>
        <NewsUpdatesPanel compact layout="ribbon" />
      </Box>

      {picker.isSupervisorPicker && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'info.50', borderLeft: '4px solid', borderColor: 'info.main' }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="subtitle1" fontWeight={700}>
              Viewing Schedule For:{' '}
              {data?.viewingTeacherName || picker.selectedTeacherName || '—'}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="supervisor-teacher-home-select">Change teacher</InputLabel>
              <Select
                labelId="supervisor-teacher-home-select"
                label="Change teacher"
                value={picker.selectedTeacherId || ''}
                onChange={(e) => picker.setSelectedTeacherId(e.target.value)}
              >
                {picker.teachers.map((t) => (
                  <MenuItem key={t.id} value={t.id}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {picker.viewingOwnSchedule || data?.viewingAsSupervisor === false
              ? 'Your teaching schedule. You can manage your own classes from here.'
              : 'Read-only view of this teacher\u2019s schedule. Switch to your name to manage your own classes.'}
          </Typography>
        </Paper>
      )}

      {data?.missingTeacherProfile && !picker.isSupervisorPicker && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid #d32f2f' }}>
          <Typography variant="body2" color="text.secondary">
            Your login is not linked to a teacher profile yet, so no batches/demos can be shown. Please ask admin to link your account in the teacher master.
          </Typography>
        </Paper>
      )}

      {showOwnNotifications && (updatesQuery.data || []).length > 0 && (
        <Paper sx={{ p: 2, mb: 3, borderLeft: '4px solid', borderColor: 'warning.main' }}>
          <Typography variant="h6" gutterBottom>
            Today&apos;s Important Updates
          </Typography>
          <List dense disablePadding>
            {(updatesQuery.data || []).map((item) => (
              <ListItemButton
                key={item.id}
                onClick={async () => {
                  try {
                    await notificationsAPI.markRead(item.id);
                    updatesQuery.refetch();
                  } catch (_) {}
                  if (item.actionUrl) {
                    const path = toAppPath(item.actionUrl);
                    if (path) navigate(path);
                  }
                }}
              >
                <ListItemText
                  primary={item.title}
                  secondary={
                    item.body
                      ? String(item.body).split('\n').slice(0, 2).join(' · ')
                      : undefined
                  }
                />
                {item.category === 'needs_action' && (
                  <Chip size="small" color="warning" label="Action" sx={{ ml: 1 }} />
                )}
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{ p: 2, borderLeft: '4px solid #1976d2', cursor: 'pointer' }}
            onClick={() => navigate('/teacher/upcoming-batches')}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Today color="primary" />
              <Typography variant="subtitle2">Upcoming Batches</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={700}>
              {data?.summary?.upcomingBatches || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{ p: 2, borderLeft: '4px solid #ed6c02', cursor: 'pointer' }}
            onClick={() => {
              const qs = new URLSearchParams({
                date_from: demosFrom,
                date_to: demosTo,
              });
              if (teacherIdForDemos) qs.set('teacher_id', teacherIdForDemos);
              navigate(`/demos?${qs.toString()}`);
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <EventNote sx={{ color: '#ed6c02' }} />
              <Typography variant="subtitle2">Demo / Trials (Next 30 days)</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={700}>
              {data?.summary?.demosTrialsNext30 ?? data?.summary?.demosTrialsToday ?? 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper sx={{ p: 2, borderLeft: '4px solid #2e7d32' }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Schedule sx={{ color: '#2e7d32' }} />
              <Typography variant="subtitle2">Weekly Sessions</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={700}>
              {data?.summary?.weeklySessions || 0}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Paper
            sx={{ p: 2, borderLeft: '4px solid #9c27b0', cursor: 'pointer' }}
            onClick={() =>
              navigate(`/teacher/makeup-classes?fromDate=${weekStart}&toDate=${weekEnd}`)
            }
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <CompareArrows sx={{ color: '#9c27b0' }} />
              <Typography variant="subtitle2">Makeup Classes</Typography>
            </Stack>
            <Typography variant="h4" fontWeight={700}>
              {Math.max(data?.summary?.makeupClasses || 0, makeupInWeekCount)}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mb: 3 }}>
        <TeacherWeeklyTimetable
          teacherId={teacherIdParam}
          viewingTeacherName={data?.viewingTeacherName || picker.selectedTeacherName}
          calendarTimezone={calendarTz}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Upcoming batches (not yet started)</Typography>
              <Button size="small" onClick={() => navigate('/teacher/upcoming-batches')} endIcon={<ArrowForward />}>
                View all
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Batches assigned to you with a start date after today. Click a row to open the batch dashboard.
            </Typography>
            <List dense>
              {(data?.upcomingBatches || []).map((b) => (
                <ListItem key={b.id} disablePadding>
                  <ListItemButton onClick={() => navigate(`/batches/${encodeURIComponent(b.id)}`)}>
                    <ListItemText
                      primary={b.name}
                      secondary={`Starts: ${String(b.start_date || b.next_session_date || '').slice(0, 10)}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {(data?.upcomingBatches || []).length === 0 ? (
                <ListItem>
                  <ListItemText primary="No batches waiting to start" />
                </ListItem>
              ) : null}
            </List>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Demos, trials &amp; makeup</Typography>
              <Button
                size="small"
                onClick={() => {
                  const qs = new URLSearchParams({
                    date_from: demosFrom,
                    date_to: demosTo,
                  });
                  if (teacherIdForDemos) qs.set('teacher_id', teacherIdForDemos);
                  navigate(`/demos?${qs.toString()}`);
                }}
                endIcon={<ArrowForward />}
              >
                Open demos
              </Button>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Click a row to open details. Makeup classes open the batch session.
            </Typography>
            <List dense>
              {(data?.upcomingDemos || []).map((d) => (
                <ListItem key={`demo-${d.id}`} disablePadding secondaryAction={<Chip size="small" label={d.status || 'scheduled'} />}>
                  <ListItemButton onClick={() => navigate(`/demos?view=${encodeURIComponent(d.id)}`)}>
                    <ListItemText
                      primary={`${d.name || 'Demo'} (${d.type})`}
                      secondary={formatDemoDualCaption(d, viewerTimezone)}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {(upcomingMakeupRows || []).map((m) => (
                <ListItem key={`makeup-${m.id}`} disablePadding secondaryAction={<Chip size="small" color="secondary" label="makeup" />}>
                  <ListItemButton
                    onClick={() =>
                      navigate(
                        `/batches/${encodeURIComponent(m.batch_id)}?sessionId=${encodeURIComponent(m.id)}`
                      )
                    }
                  >
                    <ListItemText
                      primary={m.batch_name || 'Batch'}
                      secondary={`${m.session_date} ${String(m.start_time || '').slice(0, 5)}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {(data?.upcomingDemos || []).length === 0 && upcomingMakeupRows.length === 0 ? (
                <ListItem>
                  <ListItemText primary="No upcoming demo, trial, or makeup sessions" />
                </ListItem>
              ) : null}
            </List>
            <Paper variant="outlined" sx={{ p: 1.5, mt: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Previous Month Summary
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip size="small" color="default" label={`Total: ${data?.demoPreviousMonthSummary?.total || 0}`} />
                <Chip size="small" color="success" label={`Completed: ${data?.demoPreviousMonthSummary?.completed || 0}`} />
                <Chip size="small" color="warning" label={`Pending: ${data?.demoPreviousMonthSummary?.pending || 0}`} />
                <Chip size="small" color="error" label={`Cancelled: ${data?.demoPreviousMonthSummary?.cancelled || 0}`} />
              </Stack>
            </Paper>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 0 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">New students assigned to your batches</Typography>
              <PersonAdd color="primary" />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Recent active assignments (last 60 days). Open the batch to see full roster.
            </Typography>
            <List dense>
              {(data?.newStudentAssignments || []).map((row) => (
                <ListItem key={row.assignment_id} disablePadding>
                  <ListItemButton onClick={() => navigate(`/batches?view=${encodeURIComponent(row.batch_id)}`)}>
                    <ListItemText
                      primary={row.student_name || 'Student'}
                      secondary={`${row.batch_name || 'Batch'} · Assigned ${row.assigned_at ? new Date(row.assigned_at).toLocaleString() : ''}`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
              {(data?.newStudentAssignments || []).length === 0 ? (
                <ListItem>
                  <ListItemText primary="No new assignments in the last 60 days" />
                </ListItem>
              ) : null}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Upcoming Deadlines</Typography>
              {!pendingQuery.isLoading && (
                <Typography
                  variant="caption"
                  color={pendingTotal ? 'warning.main' : 'success.main'}
                  sx={{ fontWeight: 600, textAlign: 'right' }}
                >
                  {pendingTotal === 0
                    ? 'No pending items'
                    : pendingTotal > displayedPendingCount
                      ? `Showing ${displayedPendingCount} of ${pendingTotal} pending items`
                      : `${pendingTotal} pending`}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {supervisorReadOnly
                ? 'Read-only summary for the selected teacher. Open items are not actionable from supervisor view.'
                : 'Click a row to open the exact pending item (attendance dialog opens directly; homework opens the homework screen).'}
            </Typography>

            {pendingQuery.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <List dense>
                {pendingRows.slice(0, 10).map((r) => {
                  const parts = getPendingSummary(r.missing);
                  return (
                    <ListItem key={r.sessionId} disablePadding>
                      <ListItemButton
                        disabled={supervisorReadOnly}
                        onClick={() => !supervisorReadOnly && navigateToPending(r)}
                      >
                        <ListItemText
                          primary={`${r.batchName || 'Batch'} · ${r.sessionDate || ''}`}
                          secondary={parts.length ? `Pending: ${parts.join(', ')}` : 'Pending'}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
                {pendingRows.length === 0 && !pendingQuery.isLoading ? (
                  <ListItem>
                    <ListItemText primary="No pending items. You're all caught up." />
                  </ListItem>
                ) : null}
              </List>
            )}
          </Paper>
        </Grid>
      </Grid>

      {!supervisorReadOnly ? (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button variant="contained" onClick={() => navigate('/teacher/leave')}>
            Leave Management
          </Button>
        </Stack>
      ) : null}
    </Box>
  );
};

export default TeacherHome;
